import {converterFromPennyToRub, genderMap, getObligationType} from '@/utils/common';
import { DealForm, dealFormToLabel } from '@/utils/services';
import {
  evidenceDocTypeToDocumentTitle,
  FieldLabel,
  FieldName,
} from '@/constants';

/*
 * fieldName: название поля
 * fieldValue: значение поля
 * fieldGroup - наименование группы полей
 */
interface DataFieldI {
  fieldName: string;
  fieldValue?: any;
  fieldGroup: string;
  fieldGroupEdit?: string;
}

const CommonServiceDetailsField = {
  HAS_PRIVILEGE: {
    fieldLabel: 'Наличие льготы',
    widthFull: true,
    fieldHide: false,
    isTextarea: false,
    isCheckbox: true,
  },
  NOTE: {
    fieldLabel: 'Примечание',
    widthFull: true,
    fieldHide: false,
    isTextarea: false,
  },
};

export const FieldGroup = {
  ACCEPTANCE_DEPOSIT_DETAILS: 'acceptanceDepositDetails',
  SUBJECT_DETAILS: 'subjectDetails',
  OBLIGATION_DOCUMENT: 'obligationDocument',
  BANK_DETAILS: 'bankDetails',
  EXECUTIVE_NOTICES_RECOVERY_DETAILS: 'executiveNoticesRecoveryDetails',
  RUZDI_DETAILS: 'ruzdiDetails',
  SECURITY_EVIDENCE_DETAILS: 'securityEvidenceDetails',
  TRANSFER_EL_DOCS_DETAILS: 'transferElDocsDetails',
  CORRECT_TRANSLATION_DETAILS: 'correctTranslationDetails',
  TRANSLATED_DOCUMENT: 'translatedDocument',
  ACCEPTANCE_ESCROW_DETAILS: 'acceptanceEscrowDetails',
  PLEDGOR_FOREIGN_ORGANIZATION: 'pledgorForeignOrganization',
  STORAGE_ACCEPT_DETAILS: 'storageAcceptDetails',
  STORAGE_ISSUE_DETAILS: 'storageIssueDetails',
  DOCUMENT_CONVERSION_DETAILS: 'documentConversionDetails',
  CONVERSION_DOCUMENT: 'conversionDocument',
  PROXY_INFO: 'proxyInfo',
  IDENTITY_DOCUMENT: 'identityDocument',
  IDENTITY_DOCUMENT_SHORT: 'identityDocumentShort',
  TRUSTEE: 'trustee',
  TRUSTEE_ORGANIZATION: 'trusteeOrganization',
  TRUSTEE_FOREIGN_ORGANIZATION: 'trusteeForeignOrganization',
  ORGANIZATION: 'organization',
  ASSIGNEE: 'assignee',
  FOREIGN_ORGANIZATION: 'foreignOrganization',
  EXTRACTION_ROD_DETAILS: 'extractionRodDetails',
  DOCUMENT: 'document',
  MRPA_BASE_AUTHORITY: 'mrpaBaseAuthority',
  MRPA_NOTARY_DOCUMENT: 'mrpaNotaryDocument',
  MRPA_ASSISTANT: 'mrpaAssistant',
  PROXY_PERSON: 'proxyPerson',
  PERSON: 'person',
  EGRN_CONFIRM_DETAILS: 'egrnConfirmDetails',
  EVIDENCE_DOC_TYPE: 'evidenceDocType',
  NOTARY_DOCUMENT: 'notaryDocument',
};

export function createDataField({
  fieldName,
  fieldValue,
  fieldGroup,
  fieldGroupEdit,
}: DataFieldI) {
  let field;
  let modifiedFieldValue;

  switch (fieldName) {
    case 'addressNote':
      field = {
        fieldLabel: 'Неформализованный адрес',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
    case 'factAddressNote':
      field = {
        fieldLabel: 'Неформализованный фактический адрес',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
    case FieldName.CREDITOR_ACCOUNT_TITLE:
      field = {
        fieldLabel:
          'Реквизиты кредитора для получения безналичных денежных средств',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
    case FieldName.CREDITOR_DETAILS_TITLE:
      field = {
        fieldLabel: 'Остальные сведения по кредитору',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
    case FieldName.IS_MRPA:
      field = {
        fieldLabel: 'Машиночитаемая доверенность',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
        isCheckbox: true,
      };
      break;
    case FieldName.MRPA_AUTHORITY_DOCUMENT:
      field = {
        fieldLabel: 'Cпособ указания доверенности в машиночитаемом виде',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      modifiedFieldValue = fieldValue
        ? 'Файл доверенности'
        : 'По Единому регистрационному номеру';
      break;
    case FieldName.MRPA_BASE_AUTHORITY:
      field = {
        fieldLabel:
          'Реквизиты документа, подтверждающие полномочия представителя',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
    case FieldName.MRPA_NOTARY_DOCUMENT:
      field = {
        fieldLabel: 'Сведения об удостоверении документа, если он удостоверен',
        widthFull: true,
        fieldHide: false,
        isTextarea: false,
      };
      break;
  }

  if (fieldGroup === FieldGroup.PERSON) {
    switch (fieldName) {
      case 'lastName':
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'firstName':
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'patronymic':
        field = {
          fieldLabel: 'Отчество',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'gender':
        field = {
          fieldLabel: 'Пол',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = ['M', 'F'].includes(fieldValue)
          ? genderMap[fieldValue]
          : null;
        break;
      case 'birthDate':
        field = {
          fieldLabel: 'Дата рождения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'address':
        field = {
          fieldLabel: 'Адрес',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'addressNote':
        field = {
          fieldLabel: 'Неформализованный адрес',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'snils':
        field = {
          fieldLabel: 'СНИЛС',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'email':
        field = {
          fieldLabel: 'Адрес электронной почты',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'phone':
        field = {
          fieldLabel: 'Контактный телефон',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regDate':
        field = {
          fieldLabel: 'Дата гос.регистрации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regPlace':
        field = {
          fieldLabel: 'Место гос.регистрации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'emailUl':
        field = {
          fieldLabel: 'Адрес электронной почты, содержащийся в ЕГРИП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isTrustee':
        field = {
          fieldLabel:
            'Сведения о представителе физического лица (при направлении заявления представителем)',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isBusinessmanApplicant':
        field = {
          fieldLabel: 'Взыскатель является индивидуальным предпринимателем',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isBusinessman':
        field = {
          fieldLabel: 'Заявитель является индивидуальным предпринимателем',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroupEdit === 'debtor') {
    switch (fieldName) {
      case 'address':
        field = {
          fieldLabel: 'Место жительства или место пребывания',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'birthPlace':
        field = {
          fieldLabel: 'Место рождения',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.ORGANIZATION) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Полное наименование',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'address':
        field = {
          fieldLabel: 'Адрес места нахождения, указанный в ЕГРЮЛ',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'factAddress':
        field = {
          fieldLabel: 'Фактический адрес',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regDate':
        field = {
          fieldLabel: 'Дата гос. регистрации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'kpp':
        field = {
          fieldLabel: 'КПП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'email':
        field = {
          fieldLabel: 'Адрес электронной почты',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'phone':
        field = {
          fieldLabel: 'Контактный номер',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isAssignee':
        field = {
          fieldLabel:
            'Лицо, действующее от имени юридического лица без доверенности',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isTrustee':
        field = {
          fieldLabel: 'Сведения о представителе юридического лица',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.FOREIGN_ORGANIZATION) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Наименование',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'address':
        field = {
          fieldLabel: 'Адрес (место нахождения) организации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'countryName':
        field = {
          fieldLabel: 'Страна',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН организации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regNumber':
        field = {
          fieldLabel: 'Регистрационый номер иностранного юридического лица',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'email':
        field = {
          fieldLabel: 'Адрес электронной почты',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isAssignee':
        field = {
          fieldLabel:
            'Лицо, действующее от имени иностранного юридического лица без доверенности',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isTrustee':
        field = {
          fieldLabel: 'Сведения о представителе иностранного юридического лица',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'previousCreditors') {
    switch (fieldName) {
      case 'changeReason':
        field = {
          fieldLabel: 'Причина изменения кредитора',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'previousDebtors') {
    switch (fieldName) {
      case 'changeReason':
        field = {
          fieldLabel: 'Причина изменения должника',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'businessmen') {
    switch (fieldName) {
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРНИП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regDate':
        field = {
          fieldLabel: 'Дата гос. регистрации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regPlace':
        field = {
          fieldLabel: 'Место гос. регистрации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'emailUl':
        field = {
          fieldLabel: 'Адрес электронной почты, содержащийся в ЕГРИП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.TRUSTEE) {
    switch (fieldName) {
      case 'id':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'lastName':
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'firstName':
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'patronymic':
        field = {
          fieldLabel: 'Отчество',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'birthDate':
        field = {
          fieldLabel: 'Дата рождения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'snils':
        field = {
          fieldLabel: 'СНИЛС',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'trusteeStatus':
        field = {
          fieldLabel: 'Должность или статус представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'email':
        field = {
          fieldLabel: 'Адрес электронной почты',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'phone':
        field = {
          fieldLabel: 'Контактный номер',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isStatusDocument':
        field = {
          fieldLabel:
            'Реквизиты документа, подтверждающего должность или статус представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isAuthorityDocument':
        field = {
          fieldLabel:
            'Реквизиты документа, подтверждающего полномочия представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'attestation':
        field = {
          fieldLabel: 'Сведения об удостоверении документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'docDate':
        field = {
          fieldLabel:
            fieldGroupEdit === FieldGroup.NOTARY_DOCUMENT
              ? FieldLabel.NOTARY_DOCUMENT_DOC_DATE
              : 'Дата выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuer':
        field = {
          fieldLabel: 'Кем выдан',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Наименование документа',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'number':
        field = {
          fieldLabel: 'Номер документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'series':
        field = {
          fieldLabel: 'Серия документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regNumber':
        field = {
          fieldLabel:
            fieldGroupEdit === FieldGroup.NOTARY_DOCUMENT
              ? FieldLabel.NOTARY_DOCUMENT_REG_NUMBER
              : 'Нотариальный регистрационный номер доверенности',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.TRUSTEE_ORGANIZATION) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Полное наименование',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.TRUSTEE_FOREIGN_ORGANIZATION) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Полное наименование',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.ASSIGNEE) {
    switch (fieldName) {
      case 'lastName':
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'patronymic':
        field = {
          fieldLabel: 'Отчество',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'snils':
        field = {
          fieldLabel: 'СНИЛС',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'birthDate':
        field = {
          fieldLabel: 'Дата рождения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'status':
        field = {
          fieldLabel: 'Должность или статус представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'email':
        field = {
          fieldLabel: 'Адрес электронной почты',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'phone':
        field = {
          fieldLabel: 'Контактный номер',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'jobPlace') {
    switch (fieldName) {
      case 'orgName':
        field = {
          fieldLabel: 'Наименование организации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'fio':
        field = {
          fieldLabel: 'ФИО индивидуального предпринимателя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'address':
        field = {
          fieldLabel: 'Место регистрации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'factAddress':
        field = {
          fieldLabel: 'Фактический адрес',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'ogrn':
        field = {
          fieldLabel: 'ОГРН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'kpp':
        field = {
          fieldLabel: 'КПП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
    if (fieldGroupEdit === 'organization') {
      switch (fieldName) {
        case 'address':
          field = {
            fieldLabel: 'Адрес места нахождения, указанный в ЕГРЮЛ',
            widthFull: true,
            fieldHide: false,
            isTextarea: false,
          };
          break;
      }
    }
  }

  if (fieldGroup === FieldGroup.IDENTITY_DOCUMENT) {
    switch (fieldName) {
      case 'id':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'isIdentityDocument':
        field = {
          fieldLabel: 'Реквизиты документа, удостоверяющего личность должника',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'codeName':
        field = {
          fieldLabel: 'Тип документа',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Тип документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'docDate':
        field = {
          fieldLabel: 'Дата выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuer':
        field = {
          fieldLabel: 'Кем выдан',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuerCode':
        field = {
          fieldLabel: 'Код подразделения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'number':
        field = {
          fieldLabel: 'Номер документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'series':
        field = {
          fieldLabel: 'Серия документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'attestation':
        field = {
          fieldLabel: 'Сведения об удостоверении документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'executiveNoticesRecoveryDetails') {
    switch (fieldName) {
      case 'notarialWritName':
        field = {
          fieldLabel: 'Вид документа',
          widthFull: true,
          fieldHide: false,
          isTextarea: true,
        };
        break;
      case 'notarialWritCode':
        field = {
          fieldLabel: 'Вид документа-код',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'agreementTypeName':
        field = {
          fieldLabel: 'Cведения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'agreementType':
        field = {
          fieldLabel: 'Cведения-код',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'agreementDate':
        field = {
          fieldLabel: 'Дата договора',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'agreementNumber':
        field = {
          fieldLabel: 'Номер договора',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'agreementRegNum':
        field = {
          fieldLabel: 'Регистрационный номер в реестре нотариальных действий',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'agreementNotary':
        field = {
          fieldLabel: 'Сведения о нотариусе, удостоверившем сделку',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'isOrDateCondition':
        field = {
          fieldLabel: 'Срок, когда обязательство должно было быть исполнено',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      // Срок, когда обязательство должно было быть исполнено, дата
      case 'timeLimitDate':
        field = {
          fieldLabel: 'Дата',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      // Срок, когда обязательство должно было быть исполнено, условие
      case 'timeLimitCondition':
        field = {
          fieldLabel: 'Условие',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'isTermCollectionTitle':
        field = {
          fieldLabel: 'Срок, за который производится взыскание',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      // Срок, за который производится взыскание, от
      case 'penaltyLimFrom':
        field = {
          fieldLabel: 'Дата',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      // Срок, за который производится взыскание, до
      case 'penaltyLimTo':
        field = {
          fieldLabel: 'Дата',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case 'isAmount':
        field = {
          fieldLabel: 'Сумма',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'penaltySum':
        field = {
          fieldLabel: 'Сумма, подлежащая взысканию',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'penaltyDebt':
        field = {
          fieldLabel: 'Сумма основного долга',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'penaltyPercent':
        field = {
          fieldLabel: 'Сумма процентов',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'penaltyForfeit':
        field = {
          fieldLabel:
            'Сумма неустойки (за исключением неустойки по кредитным договорам)',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'penaltyCosts':
        field = {
          fieldLabel:
            'Сумма расходов, понесенных взыскателем в связи с совершением исполнительной надписи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      // Сведения о подлежащем истребованию имуществе и его идентифицирующих признаках
      case 'demandOwn':
        field = {
          fieldLabel: 'Сведения',
          widthFull: true,
          fieldHide: false,
          isTextarea: true,
        };
        break;

      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
      case FieldName.executiveNoticesRecoveryDetails.FSSP_SUBDIVISION:
        field = {
          fieldLabel:
            'Структурное подразделение территориального органа ФССП России',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_ANNOUNCEMENT:
        field = {
          fieldLabel:
              'Заявитель подтверждает, что на момент подачи настоящего заявления между ним и должником не имеется споров, в том числе в суде',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.executiveNoticesRecoveryDetails.EDOC_TO_FOIV:
        field = {
          fieldLabel:
            'Прошу после совершения нотариального действия передать электронный документ, на котором совершена исполнительная надпись в электронной форме, в федеральный орган исполнительной власти, осуществляющий функции по обеспечению установленного порядка деятельности судов, исполнению судебных актов, актов других органов и должностных лиц, правоприменительные функции и функции по контролю и надзору в установленной сфере деятельности, посредством единой информационной системы нотариата с использованием единой системы межведомственного электронного взаимодействия',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'isDemandOwnDetails':
        field = {
          fieldLabel:
            'Сведения о подлежащем истребованию имуществе и его идентифицирующих признаках',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroupEdit === 'noticesRecoveryStep5') {
    switch (fieldName) {
      case 'notarialWritName':
        field = {
          fieldLabel: 'Вид документа',
          widthFull: true,
          fieldHide: true,
          isTextarea: true,
        };
        break;
    }
  }

  if (fieldGroup === 'judgementCreditorBankDetails') {
    switch (fieldName) {
      case 'isJudgementCreditorBankDetails':
        field = {
          fieldLabel: 'Реквизиты взыскателя для получения денежных средств',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'jdgmntCreditorName':
        field = {
          fieldLabel: 'Наименование получателя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'accountNumber':
        field = {
          fieldLabel: 'Номер счета',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'corrAccountNumber':
        field = {
          fieldLabel: 'Номер корреспондентского счета',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'bic':
        field = {
          fieldLabel: 'БИК',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'bankName':
        field = {
          fieldLabel: 'Наименование банка получателя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'kpp':
        field = {
          fieldLabel: 'КПП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.BANK_DETAILS) {
    switch (fieldName) {
      case 'recipientName':
        field = {
          fieldLabel: 'Наименование получателя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'amount':
        field = {
          fieldLabel: 'Размер денежных средств в рублях',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = converterFromPennyToRub(fieldValue);
        break;
      case 'accountNumber':
        field = {
          fieldLabel: 'Номер счета',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'corrAccountNumber':
        field = {
          fieldLabel: 'Номер корреспондентского счета',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'bankName':
        field = {
          fieldLabel: 'Наименование банка получателя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'bic':
        field = {
          fieldLabel: 'БИК',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'kpp':
        field = {
          fieldLabel: 'КПП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.MRPA_BASE_AUTHORITY) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Наименование документа',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'authorityInfo':
        field = {
          fieldLabel: 'Полномочия представителя',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issueDate':
        field = {
          fieldLabel: 'Дата выдачи доверенности',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuer':
        field = {
          fieldLabel: 'Кем выдан',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issueExpirationDate':
        field = {
          fieldLabel: 'Срок действия доверенности',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issueNumber':
        field = {
          fieldLabel: 'Единый регистрационный номер доверенности',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.MRP_ISSUER_INN:
        field = {
          fieldLabel: 'ИНН лица, выдавшего доверенность',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'systemId':
        field = {
          fieldLabel:
            'Идентифицирующие сведения об информационной системе, которая\n' +
            '          предоставляет техническую возможность получения информации о досрочном\n' +
            '          прекращении действия доверенности, в том числе в силу ее отмены\n' +
            '          доверителем',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regNumber':
        field = {
          fieldLabel: 'Реестровый номер доверенности',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'locationStep') {
    switch (fieldName) {
      case 'regionName':
        field = {
          fieldLabel: 'Субъект РФ',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'notaryName':
        field = {
          fieldLabel: 'Округ',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.RUZDI_DETAILS) {
    switch (fieldName) {
      case 'creationReferenceNumber':
        field = {
          fieldLabel:
            'Регистрационный номер уведомления о залоге движимого имущества',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isCreationReferenceNumber':
        field = {
          fieldLabel:
            'Получение информации по номеру уведомления о залоге движимого имущества',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === 'pledgorOrganization') {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Полное наименование',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === 'pledgorPerson') {
    switch (fieldName) {
      case 'lastName':
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'firstName':
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'patronymic':
        field = {
          fieldLabel: 'Отчество',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'birthDate':
        field = {
          fieldLabel: 'Дата рождения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'ogrn':
        field = {
          fieldLabel: 'ОГРНИП',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regionName':
        field = {
          fieldLabel: 'Субъект Российской Федерации',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regionCode':
        field = {
          fieldLabel: 'Субъект Российской Федерации',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.PLEDGOR_FOREIGN_ORGANIZATION) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Полное наименование (буквами русского алфавита)',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'regNumber':
        field = {
          fieldLabel:
            'Регистрационный номер юридического лица в стране его регистрации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН (при наличии)',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.SECURITY_EVIDENCE_DETAILS) {
    switch (fieldName) {
      case 'infoLocatedUrl':
        field = {
          fieldLabel:
            'Адрес в информационно-телекоммуникационной сети "Интернет", по которому находится информация, требующая осмотра',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'accessInfo':
        field = {
          fieldLabel:
            'Сведения (учетные данные и другие данные), необходимые для доступа к этой информации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'informationExamined':
        field = {
          fieldLabel:
            'Информация, подлежащая осмотру, ее объем и особенности доступа к информации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.SUBJECT_DETAILS) {
    switch (fieldName) {
      case 'information':
        field = {
          fieldLabel: 'Иные сведения о кредиторе',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'depositAmount':
        field = {
          fieldLabel: 'Размер денежных средств в рублях',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = converterFromPennyToRub(fieldValue);
        break;
      case 'depositCurrency':
        field = {
          fieldLabel:
            'Cимвольный код валюты в соответствии с Общероссийским классификатором валют (в текущей версии не отобржается пользовтаелю, но на бэк передается значение Руб)',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'placePerformanceObligation':
        field = {
          fieldLabel:
            'Место исполнения обязательства в соответствии с требованиями законодательства Российской Федерации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'transferTimeLimit':
        field = {
          fieldLabel: 'Срок передачи кредитору безналичных денежных средств',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'transferCondition':
        field = {
          fieldLabel: 'Условия передачи кредитору безналичных денежных средств',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  let type
  if (fieldGroup === FieldGroup.ACCEPTANCE_DEPOSIT_DETAILS) {
    switch (fieldName) {
      case 'depositAmount':
        field = {
          fieldLabel:
            'Общий размер вносимых денежных средств (если несколько кредиторов) в рублях',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = converterFromPennyToRub(fieldValue);
        break;
      case 'depositCurrency':
        field = {
          fieldLabel:
            'Cимвольный код валюты вносимых денежных средств в соответствии с Общероссийским классификатором валют',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'isEasement':
        type = getObligationType('isEasement')
        field = {
          fieldLabel:
            'Иные основания для внесения безналичных денежных средств в депозит нотариуса, предусмотренные законодательством Российской Федерации',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = fieldValue ? type.text : 'Не выбрано'
        break;
      case 'informationAboutObligation':
        field = {
          fieldLabel:
            'Сведения об основании для внесения в депозит',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.OBLIGATION_DOCUMENT) {
    switch (fieldName) {
      case FieldName.obligationDocument.IS_OBLIGATION_DOCUMENT:
        field = {
          fieldLabel:
            'Cведения о документе, представляющем собой содержание сделки',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Наименование',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'docDate':
        field = {
          fieldLabel: 'Дата выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'number':
        field = {
          fieldLabel: 'Номер',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'attestation':
        field = {
          fieldLabel:
            'Сведения об удостоверении документа, если он удостоверен',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.TRANSFER_EL_DOCS_DETAILS) {
    switch (fieldName) {
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.CORRECT_TRANSLATION_DETAILS) {
    switch (fieldName) {
      case 'translationLanguageFrom':
        field = {
          fieldLabel: 'Язык, с которого совершается перевод',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'translationLanguageTo':
        field = {
          fieldLabel: 'Язык, на который совершается перевод',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.TRANSLATED_DOCUMENT) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Наименование документа, подлежащего переводу',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.ACCEPTANCE_ESCROW_DETAILS) {
    switch (fieldName) {
      case FieldName.acceptanceEscrowDetails.DEAL_FORM:
        field = {
          fieldLabel: null,
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = dealFormToLabel[fieldValue];
        break;
      case FieldName.acceptanceEscrowDetails.DEAL_INFO_REG_NUM:
        field = {
          fieldLabel:
            fieldGroupEdit === DealForm.SIMPLE
              ? 'Номер'
              : 'Регистрационный номер в реестре нотариальных действий',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.acceptanceEscrowDetails.DEAL_INFO_NOTARY_INFO:
        field = {
          fieldLabel: 'Cведения о нотариусе, удостоверившем сделку',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.acceptanceEscrowDetails.DEPOSIT_AMOUNT:
        field = {
          fieldLabel: 'Общий размер вносимых денежных средств',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        modifiedFieldValue = converterFromPennyToRub(fieldValue);
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.acceptanceEscrowDetails.DEAL_INFO_DATE:
        field = {
          fieldLabel: 'Дата совершения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.STORAGE_ACCEPT_DETAILS) {
    switch (fieldName) {
      case FieldName.storageAcceptDetails.STORAGE_PERIOD_TO:
        field = {
          fieldLabel: 'Срок хранения по',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.STORAGE_ISSUE_DETAILS) {
    switch (fieldName) {
      case FieldName.storageIssueDetails.STORAGE_FILE_ID:
        field = {
          fieldLabel: 'Идентификатор хранилища',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.storageAcceptDetails.STORAGE_PERIOD_TO:
        field = {
          fieldLabel: 'Срок хранения по',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;

      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.DOCUMENT_CONVERSION_DETAILS) {
    switch (fieldName) {
      case 'fileFormat':
        field = {
          fieldLabel:
            'Формат электронного документа, в который осуществляется конвертация',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.CONVERSION_DOCUMENT) {
    switch (fieldName) {
      case 'name':
        field = {
          fieldLabel: 'Наименование файла',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.PROXY_INFO) {
    switch (fieldName) {
      case 'number':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'issueDate':
        field = {
          fieldLabel: 'Дата выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issueNumber':
        field = {
          fieldLabel: 'Номер доверенности',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuePeriod':
        field = {
          fieldLabel: 'Период выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.ISSUE_PERIOD_FROM:
        field = {
          fieldLabel: 'Дата начала периода',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.ISSUE_PERIOD_TO:
        field = {
          fieldLabel: 'Дата окончания периода',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.SUMMARY:
        field = {
          fieldLabel: 'Краткое описание',
          widthFull: true,
          fieldHide: false,
          isTextarea: true,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.EXTRACTION_ROD_DETAILS) {
    switch (fieldName) {
      case 'orderNumber':
        field = {
          fieldLabel: 'Регистрационный номер',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.DOCUMENT) {
    switch (fieldName) {
      case 'id':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'isStatusDocument':
        field = {
          fieldLabel:
            'Реквизиты документа, подтверждающего должность или статус представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'isAuthorityDocument':
        field = {
          fieldLabel:
            'Реквизиты документа, подтверждающего полномочия представителя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Наименование документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'docDate':
        field = {
          fieldLabel: 'Дата выдачи',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'issuer':
        field = {
          fieldLabel: 'Кем выдан',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'number':
        field = {
          fieldLabel: 'Номер документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'series':
        field = {
          fieldLabel: 'Серия документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'attestation':
        field = {
          fieldLabel: 'Сведения об удостоверении документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.MRPA_NOTARY_DOCUMENT) {
    switch (fieldName) {
      case 'notaryJob': {
        field = {
          fieldLabel: 'Должность нотариуса',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'notaryRegNumber': {
        field = {
          fieldLabel: 'Регистрационный номер нотариуса',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'notaryFirstName': {
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'notaryLastName': {
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'notaryPatronymic': {
        field = {
          fieldLabel: 'Отчество (при наличии)',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
    }
  }

  if (fieldGroup === FieldGroup.MRPA_ASSISTANT) {
    switch (fieldName) {
      case 'assistantJob': {
        field = {
          fieldLabel: 'Должность ВРИО',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'assistantRegNumber': {
        field = {
          fieldLabel:
            'Регистрационный номер лица, сдавшего квалификационный экзамен в Минюсте',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'assistantFirstName': {
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'assistantLastName': {
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
      case 'assistantPatronymic': {
        field = {
          fieldLabel: 'Отчество (при наличии)',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      }
    }
  }

  if (fieldGroup === FieldGroup.PROXY_PERSON) {
    switch (fieldName) {
      case 'lastName':
        field = {
          fieldLabel: 'Фамилия',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'firstName':
        field = {
          fieldLabel: 'Имя',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'patronymic':
        field = {
          fieldLabel: 'Отчество',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'birthDate':
        field = {
          fieldLabel: 'Дата рождения',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'snils':
        field = {
          fieldLabel: 'СНИЛС',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'inn':
        field = {
          fieldLabel: 'ИНН',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'phone':
        field = {
          fieldLabel: 'Контактный телефон',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.IDENTITY_DOCUMENT_SHORT) {
    switch (fieldName) {
      case 'id':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'name':
        field = {
          fieldLabel: 'Тип документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'docDate':
        field = {
          fieldLabel: 'Дата выдачи',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'issuer':
        field = {
          fieldLabel: 'Кем выдан',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'issuerCode':
        field = {
          fieldLabel: 'Код подразделения',
          widthFull: false,
          fieldHide: true,
          isTextarea: false,
        };
        break;
      case 'number':
        field = {
          fieldLabel: 'Номер документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'series':
        field = {
          fieldLabel: 'Серия документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
      case 'attestation':
        field = {
          fieldLabel: 'Сведения об удостоверении документа',
          widthFull: false,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  if (fieldGroup === FieldGroup.EGRN_CONFIRM_DETAILS) {
    switch (fieldName) {
      case 'estateObjectInfoRequest':
        field = {
          fieldLabel: 'Об объекте недвижимости',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'mainCharacteristicsRights':
        field = {
          fieldLabel:
            'Об основных характеристиках и зарегистрированных правах на объект недвижимости',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'transferRights':
        field = {
          fieldLabel: 'О переходе прав на объект недвижимости',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'contractsSharedEquityConstruction':
        field = {
          fieldLabel:
            'О зарегистрированных договорах участия в долевом строительстве',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'isTransferOfRights':
        field = {
          fieldLabel:
              'О переходе прав',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: true,
        };
        break;
      case 'estateObjectKN':
        field = {
          fieldLabel: 'Объект недвижимости (кадастровый номер)',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: false,
        };
        break;
      case 'rfRegion':
        field = {
          fieldLabel: '',
          widthFull: true,
          fieldHide: true,
          isTextarea: false,
          isCheckbox: false,
        };
        break;
      case 'rfRegionName':
        field = {
          fieldLabel:
            'Субъект Российской Федерации в котором находится объект недвижимости',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
          isCheckbox: false,
        };
        break;
      case 'dealType':
        field = {
          fieldLabel: '',
          widthFull: false,
          fieldHide: true,
          isTextarea: true,
          isCheckbox: false,
        };
        break;
      case 'violatedRights':
        field = {
          fieldLabel:
            'Нарушенные права, для защиты которых требуется информация',
          widthFull: false,
          fieldHide: false,
          isTextarea: true,
          isCheckbox: false,
        };
        break;
      case FieldName.CommonServiceDetailsField.HAS_PRIVILEGE:
        field = CommonServiceDetailsField.HAS_PRIVILEGE;
        break;
      case FieldName.CommonServiceDetailsField.NOTE:
        field = CommonServiceDetailsField.NOTE;
        break;
    }
  }

  if (fieldGroup === FieldGroup.EVIDENCE_DOC_TYPE) {
    switch (fieldName) {
      case 'evidenceDocType':
        field = {
          fieldLabel: 'Документ',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        // @ts-ignore
        modifiedFieldValue = evidenceDocTypeToDocumentTitle[fieldValue];
        break;
      case 'dealType':
        field = {
          fieldLabel:
            'Вид сделки в отношении объекта недвижимости, для совершения которой необходимы сведения',
          widthFull: true,
          fieldHide: false,
          isTextarea: false,
        };
        break;
    }
  }

  return {
    ...field,
    fieldName: fieldName || null,
    fieldValue: modifiedFieldValue ?? fieldValue ?? null,
  };
}
